import type { ReactNode } from "react";

type HudWindowProps = {
  title: string;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  showTrafficLights?: boolean;
  /** Extra nodes in the title bar after the window label (e.g. a LIVE badge). */
  titleBarExtra?: ReactNode;
};

export function HudWindow({
  title,
  children,
  className = "",
  bodyClassName = "",
  showTrafficLights = true,
  titleBarExtra,
}: HudWindowProps) {
  return (
    <div className={`relative w-full ${className}`}>
      <div
        className="pointer-events-none absolute -inset-4 rounded-[1.75rem] bg-gradient-to-br from-ownly-accent/18 via-transparent to-[var(--ownly-violet-soft)] blur-3xl sm:-inset-6 sm:rounded-[2rem]"
        aria-hidden
      />
      <div className="relative rounded-2xl p-px ownly-spec-deck-ring shadow-[0_32px_100px_-40px_rgba(0,0,0,0.92),0_0_100px_-40px_rgba(43,127,255,0.22)]">
        <div className="relative overflow-hidden rounded-[15px] border border-white/[0.13] bg-ownly-chrome shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_40px_-18px_rgba(43,127,255,0.12)] backdrop-blur-2xl">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.35] mix-blend-overlay ownly-spec-scanlines"
            aria-hidden
          />
          {showTrafficLights ? (
            <div className="relative flex items-center gap-2 border-b border-white/[0.09] px-4 py-3 md:px-5">
              <span className="size-2.5 shrink-0 rounded-full bg-[#ff5f57]/95 shadow-[0_0_8px_rgba(255,95,87,0.45)]" />
              <span className="size-2.5 shrink-0 rounded-full bg-[#febc2e]/95 shadow-[0_0_8px_rgba(254,188,46,0.35)]" />
              <span className="size-2.5 shrink-0 rounded-full bg-[#28c840]/95 shadow-[0_0_8px_rgba(40,200,64,0.35)]" />
              <div className="ml-auto flex items-center gap-2.5">
                {titleBarExtra}
                <span className="font-mono text-[13px] leading-[20px] tracking-wide text-white/60">
                  {title}
                </span>
              </div>
            </div>
          ) : null}
          <div className={`relative px-5 py-6 sm:px-6 sm:py-8 md:px-8 md:py-9 ${bodyClassName}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
